import {useEffect, useState} from "react";
import SwitchView from "@/components/SwitchView";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import Checkbox from "@mui/material/Checkbox";
import Radio from "@mui/material/Radio";
import {MatchAPI} from "@/api/match";

function HandleAttendanceModal({
	open,
	confirm,
	close,
	initialData,
	existingPlayerIds = [],
}) {
	const [insertType, setInsertType] = useState("multiple");
	const [rosters, setRosters] = useState([]);
	const [source, setSource] = useState(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [result, setResult] = useState({players: [], hasMore: false});
	const [selected, setSelected] = useState({});
	const [sourcesLoading, setSourcesLoading] = useState(true);
	const [candidatesLoading, setCandidatesLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [sourcesError, setSourcesError] = useState("");
	const [retry, setRetry] = useState(0);
	const matchId = initialData?.matchId;
	const side = initialData?.matchSide;
	useEffect(() => {
		if (!open) return;
		let active = true;
		setSourcesLoading(true);
		setSourcesError("");
		MatchAPI.getConvocationRosters(matchId, side)
			.then((data) => {
				if (active) setRosters(data || []);
			})
			.catch((err) => {
				if (active)
					setSourcesError(
						err?.response?.data?.error ||
							"Impossibile caricare le rose abilitate.",
					);
			})
			.finally(() => {
				if (active) setSourcesLoading(false);
			});
		return () => {
			active = false;
		};
	}, [open, matchId, side, retry]);
	useEffect(() => {
		if (!open) return;
		let active = true;
		setResult({players: [], hasMore: false});
		setError("");
		const canSearch =
			insertType === "multiple"
				? !!source?.value
				: !!source?.value || search.trim().length >= 2;
		if (!canSearch) {
			setCandidatesLoading(false);
			return;
		}
		setCandidatesLoading(true);
		const timer = setTimeout(() => {
			MatchAPI.getConvocationCandidates(matchId, {
				matchSide: side,
				rosterId: source?.value || undefined,
				search: search.trim() || undefined,
				page,
			})
				.then((data) => {
					if (active) setResult(data || {players: [], hasMore: false});
				})
				.catch((err) => {
					if (active)
						setError(
							err?.response?.data?.error ||
								"Impossibile cercare i convocabili.",
						);
				})
				.finally(() => {
					if (active) setCandidatesLoading(false);
				});
		}, 300);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [open, matchId, side, source?.value, search, page, insertType, retry]);
	const candidates = (result.players || []).filter(
		(p) => !existingPlayerIds.includes(p.playerId),
	);
	const selection = Object.values(selected).filter(
		(p) => !existingPlayerIds.includes(p.playerId),
	);
	const busy = sourcesLoading || candidatesLoading || saving;
	const choose = (player, checked) =>
		setSelected((prev) => {
			if (insertType === "single") return {[player.playerId]: player};
			const next = {...prev};
			if (checked) next[player.playerId] = player;
			else delete next[player.playerId];
			return next;
		});

	const SelectControl = insertType === "multiple" ? Checkbox : Radio;

	return (
		<VLFModal
			open={open}
			maxWidth="md"
			title="Aggiungi convocati"
			close={() => !saving && close()}
			confirmLabel="Aggiungi convocati"
			disabled={busy || !!error || !!sourcesError || !selection.length}
			confirm={async () => {
				if (!selection.length || busy) return;
				setSaving(true);
				try {
					const options = selection.map((p) => ({
						value: p.playerId,
						label: p.playerName,
						sourceRosterId: p.sourceRosterId,
					}));
					await confirm(
						{player: insertType === "multiple" ? options : options[0]},
						"Convocato",
						insertType,
					);
				} finally {
					setSaving(false);
				}
			}}
			content={
				<div className="cv-add-modal">
					<SwitchView
						currentSwitchView={insertType}
						setCurrentSwitchView={(mode) => {
							setInsertType(mode);
							setSelected({});
							setPage(1);
						}}
						pageOptions={[
							{value: "multiple", label: "Multiplo"},
							{value: "single", label: "Singolo"},
						]}
						fixed
					/>
					<VLFSelectNew
						aria-label="Rosa di provenienza"
						isDisabled={saving || sourcesLoading}
						label={
							insertType === "multiple"
								? "Rosa di provenienza"
								: "Rosa di provenienza (facoltativa)"
						}
						options={rosters.map((r) => ({value: r.id, label: r.name}))}
						value={source}
						isClearable
						onChange={(value) => {
							setSource(value);
							setSelected({});
							setPage(1);
						}}
						required={insertType === "multiple"}
					/>
					<VLFTextField
						disabled={saving}
						size="small"
						margin="none"
						inputProps={{"aria-label": "Cerca giocatore"}}
						label="Cerca giocatore"
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
							if (insertType === "single") setSelected({});
						}}
					/>
					{sourcesError || error ? (
						<div role="alert">
							{sourcesError || error}{" "}
							<button type="button" onClick={() => setRetry((n) => n + 1)}>
								Riprova
							</button>
						</div>
					) : null}
					{sourcesLoading || candidatesLoading ? (
						<p role="status">Caricamento convocabili...</p>
					) : !source &&
					  (insertType === "multiple" || search.trim().length < 2) ? (
						<p className="cv-add-modal__hint">
							{insertType === "multiple"
								? "Scegli una rosa per selezionare i giocatori da convocare."
								: "Cerca per nome (almeno due caratteri) oppure scegli una rosa."}
						</p>
					) : !candidates.length && !error ? (
						<p>
							Nessun giocatore disponibile. I convocati e i giocatori già in
							distinta sono esclusi.
						</p>
					) : null}
					{!sourcesLoading && !candidatesLoading && candidates.length > 0 && (
						<>
							{insertType === "multiple" && (
								<label className="cv-add-modal__all">
									<Checkbox
										size="small"
										inputProps={{"aria-label": "Seleziona questa pagina"}}
										checked={candidates.every((p) => !!selected[p.playerId])}
										onChange={(e) => {
											const next = {...selected};
											candidates.forEach((p) => {
												if (e.target.checked) next[p.playerId] = p;
												else delete next[p.playerId];
											});
											setSelected(next);
										}}
									/>{" "}
									Seleziona questa pagina
								</label>
							)}
							<div className="cv-add-modal__players">
								{candidates.map((p) => (
									<label
										className="cv-add-modal__player"
										key={`${p.sourceRosterId}:${p.playerId}`}
									>
										<SelectControl
											size="small"
											name="convocation-player"
											inputProps={{
												"aria-label": `Seleziona ${p.playerName} - ${p.rosterName}`,
											}}
											checked={
												selected[p.playerId]?.sourceRosterId ===
												p.sourceRosterId
											}
											onChange={(e) => choose(p, e.target.checked)}
										/>
										<span>
											<strong>{p.playerName}</strong>
											<span className="cv-add-modal__meta">
												{[p.rosterName, p.role, p.playerYear]
													.filter(Boolean)
													.join(" · ")}
											</span>
										</span>
									</label>
								))}
							</div>
						</>
					)}
					<div className="cv-add-modal__footer">
						<span role="status">{selection.length} selezionati</span>
						<div>
							<button
								type="button"
								disabled={busy || page === 1}
								onClick={() => setPage((n) => n - 1)}
							>
								Precedente
							</button>
							<span>Pagina {page}</span>
							<button
								type="button"
								disabled={busy || !result.hasMore}
								onClick={() => setPage((n) => n + 1)}
							>
								Successiva
							</button>
						</div>
					</div>
					<p className="cv-add-modal__hint">
						Verranno aggiunti come Convocati. Titolari, riserve e presenze si
						definiscono successivamente.
					</p>
				</div>
			}
		/>
	);
}

export default HandleAttendanceModal;
